// MDMFieldsConfig.service.ts

import { makeCygnusApiCall } from "../../../api/api";
import { UpdateFieldsPayload } from "./MDMFieldsConfig.utils";

/**
 * Fetches all available fields for client data management.
 *
 * @param categoryCode - Optional CategoryCode filter (e.g. "PARTNER_INFO").
 *   When a firm has its own field for the same FieldCode, it's returned in
 *   place of the default one rather than alongside it.
 * @returns {Promise<any[]>} A list of available fields.
 */
export const getAllFields = async (categoryCode?: string): Promise<any[]> => {
  let url = "/onboarding/all_available_fields/";
  if (categoryCode) url += `?CategoryCode=${categoryCode}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    url,
    show_message: false,
    customized_success_message: "Fetched all available fields.",
    customized_failure_message: "Failed to fetch available fields. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message: "Error occured while fetching available fields, please contact support and attach a screenshot.",
    forMDM: true,
  });

  return response.success ? response.data || [] : [];
};

/**
 * Fetches the selected fields already configured by the firm.
 *
 * @returns {Promise<any[]>} A list of selected fields.
 */
export const getSelectedFields = async (): Promise<any[]> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: "/onboarding/firm_selected_fields/",
    show_message: false,
    customized_success_message: "Fetched selected fields.",
    customized_failure_message: "Failed to fetch selected fields. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message: "Error occured while fetching selected fields, please contact support and attach a screenshot.",
    forMDM: true,
  });

  return response.success ? response.data || [] : [];
};

/**
 * Saves the selected field configuration for the given client type.
 *
 * @param clientType - The client type ("INDIVIDUAL" | "ENTITY").
 * @param fieldIds - An array of field IDs to save.
 * @returns {Promise<{ success: boolean }>} Save operation status.
 */
export const saveSelectedFields = async (
  clientType: "INDIVIDUAL" | "ENTITY",
  fieldCodes: string[]
): Promise<{ success: boolean }> => {
  const payload: UpdateFieldsPayload = {
    codes: fieldCodes,
    Update: true,
    ClientType: clientType,
  };

  const response = await makeCygnusApiCall({
    type: "POST",
    url: "/onboarding/firm_selected_fields/",
    payload,
    show_message: true,
    customized_success_message: "Field configuration saved successfully.",
    customized_failure_message: "Failed to save selected fields. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message: "Error occured while saving selected fields, please contact support and attach a screenshot.",
    forMDM: true,
  });

  return { success: response.success };
};

/**
 * Fetches the list of available field categories for dropdown filters.
 *
 * @returns {Promise<any[]>} A list of client field categories.
 */
export const getFieldCategories = async (): Promise<any[]> => {
  const response = await makeCygnusApiCall({
    type: "GET",
    url: "/reference_data?ReferenceDataDomain=ClientFieldCategory",
    show_message: false,
    customized_success_message: "Fetched field categories.",
    customized_failure_message: "Failed to fetch field categories. If the problem persists, please contact support and attach a screenshot.",
    customized_error_message: "Error occured while fetching field categories, please contact support and attach a screenshot.",
    forMDM: true,
  });

  return response.success ? response.data || [] : [];
};
